import { Module } from 'vuex';

import {
	RootState,
	ProfileState,
	ProfilePreferences,
	InboxMessage,
} from '../types';
import { api } from '../../plugins/salut-api';
import { LocaleCode, Ngo, NgoApprovalStatus } from '../../types';
import { i18n } from '../../lib';
import cloneDeep from 'lodash/cloneDeep';

const SET_PROFILE = 'SET_PROFILE';
const SET_UNREAD_INBOX_MESSAGES = 'SET_UNREAD_INBOX_MESSAGES';
const SET_ONBOARDING = 'SET_ONBOARDING';
const RESET_STATE = 'RESET_STATE';
const SET_TABLE_COLUMN_SETTINGS = 'SET_TABLE_COLUMN_SETTINGS';
const SET_LOADING = 'SET_LOADING';
const SET_LAST_ERROR = 'SET_LAST_ERROR';
const SET_NGO = 'SET_NGO';

export const ProfileActionKeys = {
	fetch: 'fetch',
	fetchUnreadInboxMessages: 'fetchUnreadInboxMessages',
	markReadInboxMessage: 'markReadInboxMessage',
	archiveInboxMessage: 'archiveInboxMessage',
	unarchiveInboxMessage: 'unarchiveInboxMessage',
	update: 'update',
	updateTableColumnSettings: 'updateTableColumnSettings',
	resetState: 'resetState',
};

export const ProfileGetterKeys = {
	loading: 'loading',
	lastError: 'lastError',
	unreadInboxMessages: 'unreadInboxMessages',
	unreadInboxMessageTotal: 'unreadInboxMessageTotal',
	basic: 'basic',
	preferences: 'preferences',
	onboarding: 'onboarding',
	tableColumnSettings: 'tableColumnSettings',
	ngo: 'ngo',
	accessCenters: 'accessCenters',
};

export interface ProfileUpdateParams extends ProfilePreferences {
	silent?: boolean;
}

const defaultPreferenceFactory = (): ProfilePreferences => ({
	language: LocaleCode.EN,
	dateFormat: 'dd/MM/yyyy',
	twoFA: false,
	saveTableColumnSettings: true,
	calendarFormat: 'standard',
	tableColumnSettings: {},
});

const initialState: ProfileState = {
	loading: false,
	lastError: '',
	// TODO: if profile not loaded, the whole basic section should be null.
	// but not doing this refactoring yet because the change is a bit too big and need a separate issue / task
	basic: {
		ngoID: 0,
		centerId: 0,
		email: '',
		staffID: '',
		userCode: '',
		name: '',
		// accessCenters being null means profile not loaded.
		// TODO: this should be removed once the above TODO is done
		accessCenters: null,
		onboarding: undefined,
		isSignupUser: false,
		roleCode: '',
	},
	preferences: defaultPreferenceFactory(),
	ngo: null,
	unreadInboxMessages: [],
	unreadInboxMessageTotal: 0,
};

const store: Module<ProfileState, RootState> = {
	namespaced: true,

	state: cloneDeep(initialState),

	actions: {
		async [ProfileActionKeys.fetch]({ commit }) {
			try {
				commit(SET_LOADING, true);

				// FIXME remove mimic delay
				await new Promise(resolve => setTimeout(resolve, 200));
				const { data } = await api.fetchProfile();
				const ngo = await api.getNgo({ ngoId: data.ngoId });
				// load all data at the same time
				commit(SET_PROFILE, data);
				commit(SET_NGO, ngo);
				commit(SET_ONBOARDING, ngo);
			} catch (error) {
				commit(SET_LAST_ERROR, error);
			} finally {
				commit(SET_LOADING, false);
			}
		},

		async [ProfileActionKeys.fetchUnreadInboxMessages]({ commit }) {
			try {
				commit(SET_LOADING, true);

				const { result, total } = await api.fetchUnreadInboxMessages();
				commit(SET_UNREAD_INBOX_MESSAGES, { result, total });
			} catch (error) {
				commit(SET_LAST_ERROR, error);
			} finally {
				commit(SET_LOADING, false);
			}
		},

		async [ProfileActionKeys.markReadInboxMessage](
			{ commit, dispatch },
			id: string,
		) {
			try {
				await api.markReadInboxMessage(id);
				dispatch(ProfileActionKeys.fetchUnreadInboxMessages);
			} catch (error) {
				commit(SET_LAST_ERROR, error);
			}
		},

		async [ProfileActionKeys.archiveInboxMessage](
			{ commit, dispatch },
			id: string,
		) {
			try {
				await api.archiveInboxMessage(id);
				dispatch(ProfileActionKeys.fetchUnreadInboxMessages);
			} catch (error) {
				commit(SET_LAST_ERROR, error);
			}
		},

		async [ProfileActionKeys.unarchiveInboxMessage](
			{ commit, dispatch },
			id: string,
		) {
			try {
				await api.unarchiveInboxMessage(id);
				dispatch(ProfileActionKeys.fetchUnreadInboxMessages);
			} catch (error) {
				commit(SET_LAST_ERROR, error);
			}
		},

		async [ProfileActionKeys.update](
			{ commit, dispatch },
			params: ProfileUpdateParams,
		) {
			try {
				commit(SET_LOADING, true);

				// FIXME remove mimic delay
				await new Promise(resolve => setTimeout(resolve, 200));
				const { data } = await api.updateProfile(params);
				const ngo = await api.getNgo({ ngoId: data.ngoId });
				// load all data at the same time
				commit(SET_PROFILE, data);
				commit(SET_NGO, ngo);
				commit(SET_ONBOARDING, ngo);

				if (!params.silent) {
					dispatch(
						'alert/success',
						i18n.t('pages.profile.actions.save.success'),
						{
							root: true,
						},
					);
				}
			} catch (error) {
				commit(SET_LAST_ERROR, error);
				const msg = [i18n.t('pages.profile.actions.save.failed')];
				if (error.statusCode !== 500) {
					msg.push(error.message);
				}
				dispatch('alert/error', msg, {
					root: true,
				});
			} finally {
				commit(SET_LOADING, false);
			}
		},

		async [ProfileActionKeys.updateTableColumnSettings](
			{ commit, dispatch },
			params: Pick<ProfilePreferences, 'tableColumnSettings'>,
		) {
			try {
				commit(SET_LOADING, true);

				const { data } = await api.updateTableColumnSettings(params);

				commit(SET_TABLE_COLUMN_SETTINGS, data);
				dispatch('alert/success', i18n.t('commonUI.dataTable.pref.success'), {
					root: true,
				});
			} catch (error) {
				commit(SET_LAST_ERROR, error);
				dispatch('alert/error', i18n.t('commonUI.dataTable.pref.failed'), {
					root: true,
				});
			} finally {
				commit(SET_LOADING, false);
			}
		},

		[ProfileActionKeys.resetState]({ commit }) {
			commit(RESET_STATE);
		},
	},

	mutations: {
		[SET_PROFILE](state, profile) {
			state.basic = {
				...state.basic,
				email: profile.email,
				ngoID: profile.ngoId,
				centerId: profile.centerId,
				staffID: profile.id,
				userCode: profile.userCode,
				name: profile.name,
				accessCenters: profile.accessCenters,
				isSignupUser: profile.isSignupUser,
				roleCode: profile.role?.code || '',
			};

			if (profile.preference) {
				state.preferences = {
					...state.preferences,

					language: profile.preference.language,
					dateFormat: profile.preference.dateFormat,
					twoFA: profile.preference.twoFA,
					saveTableColumnSettings: profile.preference.saveTableColumnSettings,
					calendarFormat: profile.preference.calendarFormat,

					// override, not merge
					tableColumnSettings: {
						...profile.preference.tableColumnSettings,
					},
				};
			} else {
				console.warn('Missing profile.preference');
				// reset to default silently so nothing appears broken to user
				state.preferences = defaultPreferenceFactory();
			}
		},

		[SET_UNREAD_INBOX_MESSAGES](
			state,
			{ result, total }: { result: InboxMessage[]; total: number },
		) {
			state.unreadInboxMessages = result;
			state.unreadInboxMessageTotal = total;
		},

		[SET_ONBOARDING](state, ngo: Ngo) {
			state.basic = {
				...state.basic,
				onboarding: Boolean(
					ngo?.signupData &&
						ngo.approvalStatus &&
						([NgoApprovalStatus.pending].includes(ngo.approvalStatus) ||
							!ngo?.onboardingData?.status?.buildYourProfileStepSubmitted ||
							!ngo?.onboardingData?.status?.tellUsMoreCompleted),
				),
			};
		},

		[RESET_STATE](state) {
			Object.assign(state, cloneDeep(initialState));
		},

		[SET_TABLE_COLUMN_SETTINGS](state, tableColumnSettings) {
			state.preferences = {
				...state.preferences,

				tableColumnSettings: {
					...tableColumnSettings,
				},
			};
		},

		[SET_LOADING](state, loading) {
			state.loading = loading;
		},

		[SET_LAST_ERROR](state, error) {
			state.lastError = error;
		},

		[SET_NGO](state, ngo) {
			state.ngo = ngo;
		},
	},

	getters: {
		[ProfileGetterKeys.loading]: state => state.loading,
		[ProfileGetterKeys.lastError]: state => state.lastError,
		[ProfileGetterKeys.unreadInboxMessages]: state => state.unreadInboxMessages,
		[ProfileGetterKeys.unreadInboxMessageTotal]: state =>
			state.unreadInboxMessageTotal,
		[ProfileGetterKeys.basic]: state => state.basic,
		[ProfileGetterKeys.preferences]: state => state.preferences,
		[ProfileGetterKeys.onboarding]: state => state.basic.onboarding,
		[ProfileGetterKeys.tableColumnSettings]: state => (tableCode: string) =>
			(state.preferences.tableColumnSettings[tableCode] ?? []).filter(
				p => p.key,
			),
		[ProfileGetterKeys.ngo]: state => state.ngo,
		[ProfileGetterKeys.accessCenters]: (_, getters) =>
			getters[ProfileGetterKeys.basic]?.accessCenters ?? null,
	},
};

export default store;
